/*
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited 2023.
 */

import { CLICK_PRODUCT_VIEW_SOURCE } from '@/data/constants/clickProductView';
import { GTMPurchasePayload } from '@/data/types/GTM';
import { OrderItem } from '@/data/types/Order';
import { dAdd, dFix } from '@/data/utils/floatingPoint';
import { isValidClickProductView } from '@/data/utils/isValidClickProductView';
import { findProductAttribute } from 'integration/utils/findProductAttribute';

const clickSourceOf = (attributes: OrderItem['orderItemExtendAttribute']) => {
	if (!Array.isArray(attributes)) {
		return undefined;
	}
	const value = attributes.find(
		(attribute) => attribute?.attributeName === 'recommended'
	)?.attributeValue;
	return isValidClickProductView(value) ? (value as CLICK_PRODUCT_VIEW_SOURCE) : undefined;
};

export const getGTMPurchaseEventData = async (payload: GTMPurchasePayload) => {
	const { cart, contextData, storeName, orgName, orgId } = payload;
	const {
		orderItem = [],
		grandTotal: cartValue = 0,
		grandTotalCurrency: cartCurrency = '',
		totalShippingCharge: cartShipping,
		totalSalesTax,
		totalShippingTax,
		orderId,
		totalAdjustment: cartAdjustments,
	} = cart;
	const hcl_account = orgId ? orgName : null;
	const cartTax = `${dAdd(dFix(totalSalesTax ?? 0), dFix(totalShippingTax ?? 0))}`;
	const marketplaceStore = storeName;
	const affiliation = 'Dportenis'; // This should be the Seller Organization name
	const products = orderItem.map(
		({
			orderItemId,
			partNumber,
			unitPrice,
			quantity,
			currency,
			salesTax,
			shippingTax,
			totalAdjustment,
			orderItemExtendAttribute,
		}) => {
			const itemContextData = contextData?.[orderItemId];
			const product = itemContextData?.product;
			const category = itemContextData?.category;
			const attributes = product?.attributes ?? [];

			return {
				id: partNumber,
				price: Number(unitPrice),
				quantity: dFix(quantity, 0),
				currency,
				affiliation,

				name: product?.name ?? '',
				category: category?.identifier ?? '',
				brand: product?.manufacturer ?? '',
				genre: findProductAttribute(attributes, 'Genero')?.value,
				family: findProductAttribute(attributes, 'Familia')?.value,
				use: findProductAttribute(attributes, 'Uso')?.value,
				size: findProductAttribute(attributes, 'Talla')?.value,

				// these properties don't exist anywhere in products response or cart response
				coupon: '', // can be derived using: adjustment.map(({code}) => code).join(', ')
				tax: `${dAdd(dFix(salesTax ?? 0), dFix(shippingTax ?? 0))}`,
				discount: Number(totalAdjustment?.value ?? 0),
				item_list_id: clickSourceOf(orderItemExtendAttribute),
			};
		}
	);
	return {
		orderId,
		cartValue: Number(cartValue),
		cartCurrency,
		cartTax,
		cartShipping,
		cartAdjustments,
		products,
		hcl_account,
		marketplaceStore,
		payMethods: cart.paymentInstruction,
	};
};
